/* istanbul ignore file */ // not fully implemented yet
import React, {
  createContext,
  useContext,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';
import { reducer, Action } from './reducer';
import { State, AdSize, DisplayAds, TemplateConfig, AdImages } from './types';

interface Context {
  dispatch: React.Dispatch<Action>;
  state: State;
}

export const DisplayAdsContext = createContext<Context>({} as Context);
export const useDisplayAdsContext = () => useContext(DisplayAdsContext);

const getImageData = (image) => {
  if (!image) {
    return image;
  }
  const newImage = { ...image };
  if (image.cropSetting && !Array.isArray(image.cropSetting)) {
    const value = newImage.cropSetting;
    newImage.cropSetting = value[0] ? [value[0]] : [value];
  }
  return newImage;
};

// when we save metadata for display ad image, color is serialized as an object.
// convert to array if needed.
const getColorArray = (color) => {
  if (!color) {
    return [];
  }

  const returnArray: any[] = [];
  let colorArray;
  if (Array.isArray(color)) {
    colorArray = color;
  }

  colorArray = Object.values(color);

  colorArray.forEach((colorValue) => {
    returnArray.push({ ...colorValue });
  });

  return returnArray;
};

const getSerializedObjectAsArray = (object: any[] | Record<string, any>) => {
  if (!object) return [];
  if (Array.isArray(object)) return object;
  return Object.values(object);
};

function initialState({ templateData, finalUrl }) {
  const { displayAdsTemplateDetails } = templateData;
  const filter = 'All sizes';
  const isAssetsLoading = false;
  const targetSizes: AdSize[] =[];
  const adSizes: AdSize[] = [];
  const displayAds: DisplayAds = {};
  const defaultAssets: DisplayAds = {};
  const templateConfigs: { [size in AdSize]?: TemplateConfig } = {};
  const adImages: AdImages = {};
  const templateId2Size: { [templateId: string]: AdSize } = {};
  const recommendedAssets = null;
  displayAdsTemplateDetails.forEach((templateSizeDetails) => {
    const { templateConfig, displayAd } = templateSizeDetails;
    const sizeKey : AdSize = `${templateConfig.width}x${templateConfig.height}` as AdSize;
    targetSizes.push(sizeKey);
    adSizes.push(sizeKey);
    displayAds[sizeKey] = {
      shortHeadline: { ...displayAd.template.shortHeadline },
      longHeadline: { ...displayAd.template.longHeadline },
      description: { ...displayAd.template.description },
      cta: { ...displayAd.template.cta },
      image: templateConfig?.image?.display ? getImageData(displayAd.template.image): {},
      logo: templateConfig?.logo?.display ? getImageData(displayAd.template.logo): {},
      background: templateConfig?.image?.display ? { ...displayAd.template.background }: {},
      color: getColorArray(displayAd.template.color),
      assetProperties: getSerializedObjectAsArray(displayAd.template.assetProperties),
    };
    defaultAssets[sizeKey] = {
      shortHeadline: { ...displayAd.template.shortHeadline },
      longHeadline: { ...displayAd.template.longHeadline },
      description: { ...displayAd.template.description },
      cta: { ...displayAd.template.cta },
      image: getImageData(displayAd.template.image),
      logo: getImageData(displayAd.template.logo),
      background: { ...displayAd.template.background },
      color: getColorArray(displayAd.template.color),
      assetProperties: getSerializedObjectAsArray(displayAd.template.assetProperties),
    };
    templateConfigs[sizeKey] = templateConfig;
    adImages[sizeKey] = {
      url: displayAd.url,
      width: templateConfig.width,
      height: templateConfig.height,
    };
    templateId2Size[templateConfig.templateId] = sizeKey;
  });

  return {
    finalUrl,
    targetSizes,
    adSizes,
    displayAds,
    defaultAssets,
    templateConfigs,
    adImages,
    recommendedAssets,
    filter,
    isAssetsLoading,
    templateId2Size,
  };
}

export const DisplayAdsProvider = ({
  children,
  templateData,
  finalUrl,
}) => {
  const [state, dispatch] = useReducer(reducer, { templateData, finalUrl }, initialState);
  const context = {
    state,
    dispatch,
  };
  return (
    <DisplayAdsContext.Provider value={context}>
      {children}
    </DisplayAdsContext.Provider>
  );
};

DisplayAdsProvider.displayName = 'DisplayAdsProvider';
DisplayAdsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  templateData: PropTypes.shape({}).isRequired,
  finalUrl: PropTypes.string,
};

DisplayAdsProvider.defaultProps = {
  finalUrl: '',
};
